import React, { useEffect, useMemo, useState } from "react";
import { Check, CirclePlus, Package, Search, Trash2, Upload } from "./virtualShopIcons.jsx";
import { MUTATION, READ, responseValue } from "./virtualShopAdminApi.js";

const keyFor = (value, fallback) => {
  const key = String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (key || fallback).slice(0, 80);
};
const requestKey = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID().replace(/-/g, "");
  }
  return "vshopconfig" + Date.now() + Math.random().toString(16).slice(2);
};
const emptyOption = (order) => ({
  key: "option-" + order, name: "", description: "", shopCreditAdjustment: 0,
  imageMediaId: null, defaultSelected: false, requiresOptionKeys: [],
  excludesOptionKeys: [], compatibilityNotes: "", order, enabled: true,
});
const emptyGroup = (order) => ({
  key: "section-" + order, name: "", description: "", selectionMode: "single",
  minSelections: 0, maxSelections: 1, order, enabled: true,
  options: [emptyOption(1)],
});
const errorText = (result, fallback) => result?.message || result?.data?.message || fallback;

function ConfigurationImport({ api, onApplied }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function runPreview() {
    if (!file) return;
    setBusy(true); setNotice(null); setConfirmed(false);
    const result = await api.previewConfigurationImport(file);
    setBusy(false);
    if (!result.ok) {
      setPreview(null);
      setNotice({ kind: "error", text: errorText(result, "The configuration CSV could not be previewed.") });
      return;
    }
    setPreview(result.data);
    setNotice({ kind: "success", text: "Read-only validation passed. Review the counts before applying." });
  }

  async function apply() {
    if (!file || !preview?.previewId || !confirmed) return;
    setBusy(true); setNotice(null);
    const result = await api.commitConfigurationImport(file, {
      previewId: preview.previewId,
      requestKey: requestKey(),
    });
    setBusy(false);
    if (!result.ok) {
      setNotice({ kind: "error", text: errorText(result, "The configuration CSV was not applied.") });
      return;
    }
    setNotice({ kind: "success", text: "Applied configuration for " + (result.data?.configuredProducts || 0) + " products." });
    setPreview(null); setConfirmed(false); setFile(null);
    onApplied?.();
  }

  return (
    <section className="vs-panel vs-config-import">
      <div className="vs-panel-heading"><div><h2>Configuration CSV Import</h2><p>Preview and atomically apply the locked VIRTUAL-SHOP-CONFIGURATION-V1 contract.</p></div></div>
      {notice ? <p className={"vs-config-notice is-" + notice.kind}>{notice.text}</p> : null}
      <div className="vs-config-import-row">
        <label className="vs-upload-button"><Upload size={17} /> Choose CSV
          <input type="file" accept=".csv,text/csv" onChange={(event) => { setFile(event.target.files?.[0] || null); setPreview(null); setConfirmed(false); }} />
        </label>
        <span>{file?.name || "No file selected"}</span>
        <button className="vs-secondary" disabled={!file || busy} onClick={runPreview}>{busy ? "Working…" : "Preview"}</button>
      </div>
      {preview ? (
        <div className="vs-config-preview">
          <strong>{preview.filename}</strong>
          <span>{preview.counts?.products || 0} products</span>
          <span>{preview.counts?.groups || 0} sections</span>
          <span>{preview.counts?.options || 0} options</span>
          <span>{preview.counts?.rows || 0} rows</span>
          <label><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> Replace the configuration definitions for these products.</label>
          <button className="vs-primary" disabled={!confirmed || busy} onClick={apply}><Check size={17} /> Apply Configuration File</button>
        </div>
      ) : null}
    </section>
  );
}

function OptionEditor({ option, onChange, onRemove }) {
  const set = (field, value) => onChange({ ...option, [field]: value });
  return (
    <article className="vs-config-option">
      <div className="vs-config-row">
        <label>Option name<input value={option.name} maxLength={180} onChange={(event) => set("name", event.target.value)} onBlur={() => !option.key.startsWith("option-") || set("key", keyFor(option.name, option.key))} /></label>
        <label>Stable key<input value={option.key} maxLength={80} onChange={(event) => set("key", keyFor(event.target.value, ""))} /></label>
        <label>Shop Credit change<input type="number" value={option.shopCreditAdjustment} onChange={(event) => set("shopCreditAdjustment", Number(event.target.value) || 0)} /></label>
        <button className="vs-icon-button is-danger" title="Remove option" onClick={onRemove}><Trash2 size={16} /></button>
      </div>
      <label>Description<textarea value={option.description} maxLength={3000} onChange={(event) => set("description", event.target.value)} /></label>
      <div className="vs-config-row">
        <label><input type="checkbox" checked={option.defaultSelected} onChange={(event) => set("defaultSelected", event.target.checked)} /> Default</label>
        <label><input type="checkbox" checked={option.enabled} onChange={(event) => set("enabled", event.target.checked)} /> Enabled</label>
        <label>Requires keys<input value={(option.requiresOptionKeys || []).join(" | ")} onChange={(event) => set("requiresOptionKeys", event.target.value.split("|").map((item) => item.trim()).filter(Boolean))} /></label>
        <label>Excludes keys<input value={(option.excludesOptionKeys || []).join(" | ")} onChange={(event) => set("excludesOptionKeys", event.target.value.split("|").map((item) => item.trim()).filter(Boolean))} /></label>
      </div>
      <label>Compatibility notes<textarea value={option.compatibilityNotes || ""} maxLength={3000} onChange={(event) => set("compatibilityNotes", event.target.value)} /></label>
    </article>
  );
}

function GroupEditor({ group, onChange, onRemove }) {
  const set = (field, value) => onChange({ ...group, [field]: value });
  const updateOption = (index, option) => set("options", group.options.map((item, itemIndex) => itemIndex === index ? option : item));
  const removeOption = (index) => set("options", group.options.filter((_item, itemIndex) => itemIndex !== index).map((item, itemIndex) => ({ ...item, order: itemIndex + 1 })));
  return (
    <section className="vs-config-group">
      <div className="vs-config-row">
        <label>Section name<input value={group.name} maxLength={160} onChange={(event) => set("name", event.target.value)} onBlur={() => !group.key.startsWith("section-") || set("key", keyFor(group.name, group.key))} /></label>
        <label>Stable key<input value={group.key} maxLength={80} onChange={(event) => set("key", keyFor(event.target.value, ""))} /></label>
        <label>Selection mode<select value={group.selectionMode} onChange={(event) => set("selectionMode", event.target.value)}><option value="single">Choose one</option><option value="multiple">Choose several</option></select></label>
        <button className="vs-icon-button is-danger" title="Remove section" onClick={onRemove}><Trash2 size={16} /></button>
      </div>
      <label>Description<textarea value={group.description} maxLength={5000} onChange={(event) => set("description", event.target.value)} /></label>
      <div className="vs-config-row">
        <label>Minimum<input type="number" min="0" max="100" value={group.minSelections} onChange={(event) => set("minSelections", Number(event.target.value) || 0)} /></label>
        <label>Maximum<input type="number" min="1" max="100" value={group.maxSelections} onChange={(event) => set("maxSelections", Number(event.target.value) || 1)} /></label>
        <label><input type="checkbox" checked={group.enabled} onChange={(event) => set("enabled", event.target.checked)} /> Enabled</label>
      </div>
      <div className="vs-config-options">
        {group.options.map((option, index) => <OptionEditor key={option.key + "-" + index} option={option} onChange={(next) => updateOption(index, next)} onRemove={() => removeOption(index)} />)}
      </div>
      <button className="vs-secondary" onClick={() => set("options", [...group.options, emptyOption(group.options.length + 1)])}><CirclePlus size={16} /> Add option</button>
    </section>
  );
}

export default function VirtualShopConfigurations({ api }) {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [definition, setDefinition] = useState({ enabled: false, groups: [] });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const selectedName = useMemo(() => selected?.name || "No product selected", [selected]);

  async function findProducts(term = search) {
    setBusy(true);
    const result = await api.read(READ.PRODUCTS, { page: 1, pageSize: 25, search: term, status: "", placement: "" });
    setBusy(false);
    if (result.ok) setProducts(responseValue(result, ["items", "products"], []));
    else setNotice({ kind: "error", text: errorText(result, "Products are unavailable.") });
  }

  async function openProduct(product) {
    setBusy(true); setNotice(null);
    const result = await api.read(READ.PRODUCT_CONFIGURATION, { productId: product.id });
    setBusy(false);
    if (!result.ok) {
      setNotice({ kind: "error", text: errorText(result, "Product configuration is unavailable.") });
      return;
    }
    setSelected(product);
    setDefinition(responseValue(result, ["configuration"], { enabled: false, groups: [] }));
  }

  async function save() {
    if (!selected) return;
    setBusy(true); setNotice(null);
    const payload = {
      enabled: Boolean(definition.enabled),
      groups: (definition.groups || []).map((group, groupIndex) => ({
        ...group, order: groupIndex + 1,
        options: (group.options || []).map((option, optionIndex) => ({ ...option, order: optionIndex + 1 })),
      })),
    };
    const result = await api.mutate(MUTATION.SAVE_PRODUCT_CONFIGURATION, { productId: selected.id }, payload);
    setBusy(false);
    if (!result.ok) {
      setNotice({ kind: "error", text: errorText(result, "Configuration was not saved.") });
      return;
    }
    setDefinition(responseValue(result, ["configuration"], payload));
    setNotice({ kind: "success", text: "Product configuration saved." });
  }

  useEffect(() => { findProducts(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const updateGroup = (index, group) => setDefinition({ ...definition, groups: definition.groups.map((item, itemIndex) => itemIndex === index ? group : item) });
  const removeGroup = (index) => setDefinition({ ...definition, groups: definition.groups.filter((_item, itemIndex) => itemIndex !== index).map((item, itemIndex) => ({ ...item, order: itemIndex + 1 })) });

  return (
    <div className="vs-screen-stack">
      <ConfigurationImport api={api} onApplied={() => selected && openProduct(selected)} />
      <section className="vs-panel">
        <div className="vs-panel-heading"><div><h2>Configurable Products</h2><p>Create opt-in upgrade sections for houses, cars, computers and other complex items.</p></div></div>
        {notice ? <p className={"vs-config-notice is-" + notice.kind}>{notice.text}</p> : null}
        <div className="vs-config-layout">
          <aside className="vs-config-products">
            <form onSubmit={(event) => { event.preventDefault(); findProducts(); }}><label className="vs-search"><Search size={17} /><input value={search} placeholder="Find a product" onChange={(event) => setSearch(event.target.value)} /></label></form>
            {products.map((product) => <button key={product.id} className={selected?.id === product.id ? "is-selected" : ""} onClick={() => openProduct(product)}><Package size={17} /><span><strong>{product.name}</strong><small>{product.shopCreditCost} Shop Credits · {product.configurationEnabled ? product.configurationGroupCount + " sections" : "standard product"}</small></span></button>)}
          </aside>
          <div className="vs-config-editor">
            <div className="vs-config-editor-heading"><div><span className="vs-eyebrow">SELECTED PRODUCT</span><h3>{selectedName}</h3></div>{selected ? <label><input type="checkbox" checked={definition.enabled} onChange={(event) => setDefinition({ ...definition, enabled: event.target.checked })} /> Customer configuration enabled</label> : null}</div>
            {!selected ? <p>Select a product to create or edit its configuration.</p> : (
              <>
                {(definition.groups || []).map((group, index) => <GroupEditor key={group.key + "-" + index} group={group} onChange={(next) => updateGroup(index, next)} onRemove={() => removeGroup(index)} />)}
                <div className="vs-actions">
                  <button className="vs-secondary" onClick={() => setDefinition({ ...definition, groups: [...definition.groups, emptyGroup(definition.groups.length + 1)] })}><CirclePlus size={17} /> Add configuration section</button>
                  <button className="vs-primary" disabled={busy} onClick={save}><Check size={17} /> {busy ? "Saving…" : "Save configuration"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
